"use client";

import {
  AudioLines,
  Bot,
  Boxes,
  Brain,
  BrainCircuit,
  Clapperboard,
  Database,
  FileScan,
  Image as ImageIcon,
  Library,
  MessagesSquare,
  Mic,
  Network,
  Palette,
  Paperclip,
  Plug,
  Search,
  SlidersHorizontal,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import { ClaudeGlyph, CodexGlyph } from "@/components/agents/agent-icons";
import type { ServiceName } from "@/components/settings/SettingsContext";

/**
 * Settings information architecture.
 *
 * Two levels, deliberately unlike the flat Learning Space dashboard:
 *   • The hub (`/settings`) shows six category blocks + a resident Status
 *     module — nothing else.
 *   • Categories with several settings (Models, Chat) open a sub-hub page
 *     that lists their leaves as tiles; single-setting categories link
 *     straight to their leaf page.
 *
 * This module is the single source for the blocks, the sub-hub tiles, and the
 * breadcrumb trail rendered top-left on every page.
 */

export type Lang = { en: string; ko: string };

export interface SettingsLeaf {
  key: string;
  href: string;
  label: Lang;
  blurb: Lang;
  icon: LucideIcon;
  /** Colored icon-tile accent for the sub-hub grid (full class strings). */
  tile: string;
  /** Model-service leaves carry a configured/not chip from the catalog. */
  service?: ServiceName;
  /** Hidden from non-admin users (the backend rejects them anyway). */
  adminOnly?: boolean;
}

export interface SettingsCategory {
  key: string;
  label: Lang;
  /** One-line descriptor shown on the hub block. */
  blurb: Lang;
  icon: LucideIcon;
  /** Where clicking the block lands — a sub-hub or a leaf page. */
  href: string;
  /** Leaves listed on the sub-hub page (omitted for direct-leaf categories). */
  children?: SettingsLeaf[];
}

const MODEL_CHILDREN: SettingsLeaf[] = [
  {
    key: "llm",
    href: "/settings/llm",
    label: { en: "LLM", ko: "LLM" },
    blurb: {
      en: "Language model providers and active profile.",
      ko: "언어 모델 공급자와 현재 프로필.",
    },
    icon: Brain,
    tile: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    service: "llm",
  },
  {
    key: "embedding",
    href: "/settings/embedding",
    label: { en: "Embedding", ko: "임베딩" },
    blurb: {
      en: "Embedding model providers and dimensions.",
      ko: "임베딩 모델 공급자와 차원 수.",
    },
    icon: Database,
    tile: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    service: "embedding",
  },
  {
    key: "search",
    href: "/settings/search",
    label: { en: "Search", ko: "검색" },
    blurb: {
      en: "Web search providers.",
      ko: "웹 검색 공급자.",
    },
    icon: Search,
    tile: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    service: "search",
  },
  {
    key: "tts",
    href: "/settings/tts",
    label: { en: "Text-to-Speech", ko: "음성 합성(TTS)" },
    blurb: {
      en: "Text-to-speech for reading replies aloud.",
      ko: "답변을 음성으로 읽어주는 TTS 공급자.",
    },
    icon: AudioLines,
    tile: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    service: "tts",
  },
  {
    key: "stt",
    href: "/settings/stt",
    label: { en: "Speech-to-Text", ko: "음성 인식(STT)" },
    blurb: {
      en: "Speech-to-text for the composer microphone.",
      ko: "채팅창 마이크 음성을 텍스트로 변환하는 STT 공급자.",
    },
    icon: Mic,
    tile: "bg-pink-500/10 text-pink-600 dark:text-pink-400",
    service: "stt",
  },
  {
    key: "imagegen",
    href: "/settings/image",
    label: { en: "Image Generation", ko: "이미지 생성" },
    blurb: {
      en: "Text-to-image model for the chat imagegen tool.",
      ko: "채팅 imagegen 도구가 사용하는 텍스트-이미지 모델.",
    },
    icon: ImageIcon,
    tile: "bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400",
    service: "imagegen",
  },
  {
    key: "videogen",
    href: "/settings/video",
    label: { en: "Video Generation", ko: "영상 생성" },
    blurb: {
      en: "Text-to-video model for the chat videogen tool.",
      ko: "채팅 videogen 도구가 사용하는 텍스트-비디오 모델.",
    },
    icon: Clapperboard,
    tile: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    service: "videogen",
  },
];

const CHAT_CHILDREN: SettingsLeaf[] = [
  {
    key: "tools",
    href: "/settings/tools",
    label: { en: "Tools", ko: "도구" },
    blurb: {
      en: "Built-in tools the chat agent can invoke.",
      ko: "대화 에이전트가 호출할 수 있는 내장 도구.",
    },
    icon: Wrench,
    tile: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  },
  {
    key: "mcp",
    href: "/settings/mcp",
    label: { en: "MCP servers", ko: "MCP 서버" },
    blurb: {
      en: "External MCP servers shared by the deployment.",
      ko: "배포 환경에서 공유하는 외부 MCP 서버.",
    },
    icon: Plug,
    tile: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    adminOnly: true,
  },
  {
    key: "capabilities",
    href: "/settings/capabilities",
    label: { en: "Capabilities", ko: "기능" },
    blurb: {
      en: "Per-capability LLM parameters and runtime knobs.",
      ko: "기능별 LLM 파라미터와 런타임 옵션.",
    },
    icon: SlidersHorizontal,
    tile: "bg-lime-500/10 text-lime-600 dark:text-lime-400",
  },
  {
    key: "attachments",
    href: "/settings/attachments",
    label: { en: "Attachments", ko: "첨부파일" },
    blurb: {
      en: "Upload caps and extraction budgets for chat attachments.",
      ko: "채팅 첨부파일의 업로드 용량 제한과 텍스트 추출 예산.",
    },
    icon: Paperclip,
    tile: "bg-teal-500/10 text-teal-600 dark:text-teal-400",
    adminOnly: true,
  },
];

const AGENT_CHILDREN: SettingsLeaf[] = [
  {
    key: "agent-claude-code",
    href: "/settings/agents/claude-code",
    label: { en: "Claude Code", ko: "Claude Code" },
    blurb: {
      en: "Model, reasoning effort, and run params for the local Claude Code.",
      ko: "AI TokTok이 로컬 Claude Code를 호출할 때 사용하는 모델, 추론 강도, 실행 파라미터.",
    },
    // Brand glyph shares the lucide call signature (size/className).
    icon: ClaudeGlyph as unknown as LucideIcon,
    tile: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
    adminOnly: true,
  },
  {
    key: "agent-codex",
    href: "/settings/agents/codex",
    label: { en: "Codex", ko: "Codex" },
    blurb: {
      en: "Model, reasoning effort, and run params for the local Codex.",
      ko: "AI TokTok이 로컬 Codex를 호출할 때 사용하는 모델, 추론 강도, 실행 파라미터.",
    },
    icon: CodexGlyph as unknown as LucideIcon,
    tile: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    adminOnly: true,
  },
];

export const SETTINGS_CATEGORIES: SettingsCategory[] = [
  {
    key: "appearance",
    label: { en: "Appearance", ko: "외관" },
    blurb: {
      en: "Theme and interface language",
      ko: "테마와 인터페이스 언어",
    },
    icon: Palette,
    href: "/settings/appearance",
  },
  {
    key: "network",
    label: { en: "Network", ko: "네트워크" },
    blurb: {
      en: "Ports, browser API base, and CORS",
      ko: "포트, 브라우저 API 주소, CORS",
    },
    icon: Network,
    href: "/settings/network",
  },
  {
    key: "models",
    label: { en: "Models", ko: "모델" },
    blurb: {
      en: "Language, embedding, search, voice, and generation models",
      ko: "언어, 임베딩, 검색, 음성, 생성 모델",
    },
    icon: Boxes,
    href: "/settings/models",
    children: MODEL_CHILDREN,
  },
  {
    key: "knowledge",
    label: { en: "Knowledge Base", ko: "책" },
    blurb: {
      en: "Document parsing engine",
      ko: "문서 파싱 엔진",
    },
    icon: Library,
    href: "/settings/document-parsing",
  },
  {
    key: "chat",
    label: { en: "Chat", ko: "채팅" },
    blurb: {
      en: "Tools, MCP servers, capabilities, and attachments",
      ko: "도구, MCP 서버, 기능, 첨부파일",
    },
    icon: MessagesSquare,
    href: "/settings/chat",
    children: CHAT_CHILDREN,
  },
  {
    key: "agents",
    label: { en: "Partners & Agents", ko: "파트너 및 에이전트" },
    blurb: {
      en: "Configure the subagents you can call on in chat",
      ko: "채팅에서 호출할 수 있는 서브에이전트 설정",
    },
    icon: Bot,
    href: "/settings/agents",
    children: AGENT_CHILDREN,
  },
  {
    key: "memory",
    label: { en: "Memory", ko: "메모리" },
    blurb: {
      en: "Chunking, budget, dedup, and reference policies",
      ko: "청킹, 예산, 중복 제거, 참조 정책",
    },
    icon: BrainCircuit,
    href: "/settings/memory",
  },
];

export const SETTINGS_HUB_HREF = "/settings";
const HUB_LABEL: Lang = { en: "Settings", ko: "설정" };

/** Routes that are pure navigation (hub + sub-hubs) — no Save/Apply toolbar. */
const NAV_ONLY_ROUTES = new Set<string>([
  SETTINGS_HUB_HREF,
  ...SETTINGS_CATEGORIES.filter((c) => c.children).map((c) => c.href),
]);

export function isNavOnlyRoute(pathname: string): boolean {
  return NAV_ONLY_ROUTES.has(pathname);
}

// The on-disk file (under data/user/settings/) each leaf module persists to.
// Surfaced in the toolbar status line so every page says where its parameters
// live, without duplicating the string on each page.
const STORAGE_PATHS: Record<string, string> = {
  "/settings/appearance": "data/user/settings/interface.json",
  "/settings/network": "data/user/settings/system.json",
  "/settings/llm": "data/user/settings/model_catalog.json",
  "/settings/embedding": "data/user/settings/model_catalog.json",
  "/settings/search": "data/user/settings/model_catalog.json",
  "/settings/tts": "data/user/settings/model_catalog.json",
  "/settings/stt": "data/user/settings/model_catalog.json",
  "/settings/image": "data/user/settings/model_catalog.json",
  "/settings/video": "data/user/settings/model_catalog.json",
  "/settings/document-parsing": "data/user/settings/document_parsing.json",
  "/settings/tools": "data/user/settings/interface.json",
  "/settings/attachments": "data/user/settings/system.json",
  "/settings/mcp": "data/user/settings/mcp.json",
  "/settings/capabilities": "data/user/settings/main.yaml · agents.yaml",
  "/settings/memory": "data/user/settings/main.yaml",
  "/settings/agents/claude-code": "data/user/settings/subagent.json",
  "/settings/agents/codex": "data/user/settings/subagent.json",
};

export function storagePathFor(pathname: string): string | null {
  return STORAGE_PATHS[pathname] ?? null;
}

export interface Crumb {
  label: Lang;
  /** Omitted on the current (last) crumb. */
  href?: string;
}

/**
 * The breadcrumb trail for a settings route, e.g.
 *   /settings/llm  →  설정 / 모델 / LLM
 *   /settings/network  →  설정 / 네트워크
 * Returns just [설정] for the hub itself.
 */
export function breadcrumbFor(pathname: string): Crumb[] {
  const root: Crumb = { label: HUB_LABEL, href: SETTINGS_HUB_HREF };
  if (pathname === SETTINGS_HUB_HREF) return [{ label: HUB_LABEL }];

  // Direct-leaf or sub-hub category landed on its own href.
  const category = SETTINGS_CATEGORIES.find((c) => c.href === pathname);
  if (category) return [root, { label: category.label }];

  // A leaf inside a sub-hub category.
  for (const c of SETTINGS_CATEGORIES) {
    const leaf = c.children?.find((l) => l.href === pathname);
    if (leaf) {
      return [root, { label: c.label, href: c.href }, { label: leaf.label }];
    }
  }

  // Unknown sub-route (e.g. a legacy redirect target rendered directly).
  return [root];
}
